import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { Overline } from "@/components/Reveal";
import { BRAND } from "@/lib/constants";

const SECTIONS = [
  {
    h: "1. Introduction",
    p: [
      `Welcome to ${BRAND.name} ("we", "us", "our"). These Terms & Conditions ("Terms") govern your access to and use of our website, products and services (collectively, the "Platform"). By browsing, registering, or placing an order on the Platform, you ("you", "user", "customer") agree to be bound by these Terms. If you do not agree, please do not use the Platform.`,
      `These Terms are published in accordance with the provisions of the Information Technology Act, 2000 and the rules made thereunder, and the Consumer Protection Act, 2019, as applicable in India.`,
    ],
  },
  {
    h: "2. Definitions",
    list: [
      `"Products" means the snacks, food items and related goods offered for sale on the Platform.`,
      `"Order" means a request placed by you to purchase Products.`,
      `"Account" means the profile or session created to place and manage orders.`,
      `"Content" means all text, images, videos, logos, graphics and material on the Platform.`,
    ],
  },
  {
    h: "3. Eligibility",
    p: [
      `You must be at least 18 years of age and legally capable of entering into binding contracts under the Indian Contract Act, 1872, to place an order. By using the Platform, you represent that you meet these requirements. Minors may use the Platform only under the supervision of a parent or legal guardian.`,
    ],
  },
  {
    h: "4. Account & Information Accuracy",
    p: [
      `You are responsible for providing accurate, current and complete information, including your name, delivery address, contact number and email. You are responsible for maintaining the confidentiality of any account credentials and for all activity that occurs under your account. We are not liable for losses arising from incorrect information supplied by you.`,
    ],
  },
  {
    h: "5. Products, Descriptions & Pricing",
    p: [
      `We make every effort to display Product details, images, flavours, weights and nutritional information as accurately as possible. Actual packaging and product appearance may vary slightly. Nutritional values are approximate and provided for reference only.`,
      `All prices are listed in Indian Rupees (₹) and are inclusive of applicable taxes unless stated otherwise. We reserve the right to modify prices, offers and product availability at any time without prior notice. In the event of an obvious pricing error, we reserve the right to cancel the affected order and refund any amount paid.`,
    ],
  },
  {
    h: "6. Orders & Acceptance",
    p: [
      `Your order constitutes an offer to purchase Products. All orders are subject to acceptance and availability. We reserve the right to accept or decline any order, in whole or in part, for reasons including but not limited to stock unavailability, errors in pricing or product information, or suspected fraudulent activity. A contract of sale is formed only when your order is confirmed and payment is successfully received.`,
    ],
  },
  {
    h: "7. Payments",
    p: [
      `Online payments on the Platform are processed securely through our payment partner, Razorpay. We accept UPI, debit/credit cards, net banking and supported wallets. We do not store your complete card or banking details on our servers.`,
      `By making a payment, you confirm that the payment instrument used is legally yours. In case of any payment failure where the amount is debited but the order is not confirmed, the amount is typically auto-refunded by the payment gateway/bank within 5–7 business days.`,
    ],
  },
  {
    h: "8. Shipping & Delivery",
    p: [
      `We aim to dispatch orders promptly and deliver within the estimated timelines communicated at checkout. Delivery timelines are indicative and may vary due to location, courier delays, weather or events beyond our control. Delivery charges, if any, are displayed at checkout; free delivery may apply above the stated order value.`,
      `Risk in the Products passes to you upon delivery to the address provided. Please ensure someone is available to receive the order.`,
    ],
  },
  {
    h: "9. Cancellations",
    p: [
      `You may request cancellation of an order before it is packed or dispatched by contacting us at ${BRAND.emails.support}. Once an order has been dispatched, it cannot be cancelled. We reserve the right to cancel orders due to stock issues, pricing errors or suspected misuse, with a full refund of any amount paid.`,
    ],
  },
  {
    h: "10. Returns & Refunds",
    p: [
      `As our Products are food and consumable items, they are generally non-returnable for hygiene and safety reasons. However, if you receive a damaged, defective, expired or incorrect item, please contact us within 48 hours of delivery at ${BRAND.emails.support} with your order number and clear photographs.`,
      `Approved refunds will be processed to the original payment method within 5–7 business days after verification. Replacement may be offered in lieu of a refund at our discretion.`,
    ],
  },
  {
    h: "11. Food Safety & Allergen Notice",
    p: [
      `Our Products may contain or be manufactured in facilities that handle soya, cereals (gluten), mustard, nuts and spices. Please read the ingredient and allergen information on each product before consuming. If you have allergies or specific dietary requirements, exercise caution. Store Products as per the storage instructions on the pack. We make no medical claims; Products are intended to be enjoyed as part of a balanced diet.`,
    ],
  },
  {
    h: "12. Intellectual Property",
    p: [
      `All Content on the Platform, including the ${BRAND.name} name, logo, packaging designs, product names, images, videos, text and graphics, is owned by or licensed to us and is protected under applicable intellectual property laws. You may not copy, reproduce, distribute, or create derivative works from any Content without our prior written consent.`,
    ],
  },
  {
    h: "13. User Conduct",
    list: [
      `You agree not to use the Platform for any unlawful or fraudulent purpose.`,
      `You will not attempt to gain unauthorised access to the Platform, its servers or systems.`,
      `You will not upload or transmit viruses, malicious code, or any content that is defamatory, obscene or infringing.`,
      `You will not use automated means to scrape, harvest or interfere with the Platform.`,
    ],
  },
  {
    h: "14. Third-Party Links",
    p: [
      `The Platform may contain links to third-party websites or services (including social media and payment gateways). We are not responsible for the content, policies or practices of such third parties. Accessing them is at your own risk.`,
    ],
  },
  {
    h: "15. Limitation of Liability",
    p: [
      `To the maximum extent permitted by law, ${BRAND.name} shall not be liable for any indirect, incidental, special or consequential damages arising out of or in connection with your use of the Platform or Products. Our total aggregate liability for any claim shall not exceed the amount paid by you for the specific order giving rise to the claim.`,
    ],
  },
  {
    h: "16. Indemnity",
    p: [
      `You agree to indemnify and hold harmless ${BRAND.name}, its directors, employees and partners from any claims, damages, losses or expenses (including legal fees) arising out of your breach of these Terms or your misuse of the Platform.`,
    ],
  },
  {
    h: "17. Force Majeure",
    p: [
      `We shall not be held liable for any failure or delay in performance caused by events beyond our reasonable control, including natural disasters, pandemics, strikes, government actions, supply-chain disruptions or courier failures.`,
    ],
  },
  {
    h: "18. Governing Law & Jurisdiction",
    p: [
      `These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising in connection with these Terms shall be subject to the exclusive jurisdiction of the competent courts in India.`,
    ],
  },
  {
    h: "19. Changes to These Terms",
    p: [
      `We may update or modify these Terms at any time. The revised Terms will be effective upon posting on the Platform. Your continued use of the Platform after any change constitutes your acceptance of the updated Terms. Please review this page periodically.`,
    ],
  },
  {
    h: "20. Contact Us",
    p: [
      `For any questions, concerns or grievances regarding these Terms, please reach out to us:`,
    ],
    contact: true,
  },
];

export default function Terms() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Terms & Conditions — Dignity Food Kitchen";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Terms & Conditions for Dignity Food Kitchen — orders, payments, shipping, returns, food safety and more.");
  }, []);

  return (
    <div className="min-h-screen bg-black pt-28">
      <div className="mx-auto max-w-4xl px-5 pb-24 lg:px-8">
        <Link to="/" className="inline-flex items-center gap-1 py-4 font-oswald text-xs uppercase tracking-[0.15em] text-white/50 hover:text-gold" data-testid="terms-back">
          <ChevronLeft size={16} /> Back to Home
        </Link>

        <div className="mt-4">
          <Overline>Legal</Overline>
        </div>
        <h1 className="mt-3 font-display text-5xl leading-none tracking-tight text-cream sm:text-7xl">
          TERMS & <span className="text-gold-gradient">CONDITIONS</span>
        </h1>
        <p className="mt-4 text-sm text-white/40">Last updated: June 2026</p>

        <div className="mt-12 space-y-10" data-testid="terms-content">
          {SECTIONS.map((s) => (
            <section key={s.h}>
              <h2 className="font-oswald text-xl uppercase tracking-wide text-gold sm:text-2xl">{s.h}</h2>
              {s.p && s.p.map((para, i) => (
                <p key={i} className="mt-3 leading-relaxed text-white/65">{para}</p>
              ))}
              {s.list && (
                <ul className="mt-3 space-y-2">
                  {s.list.map((li, i) => (
                    <li key={i} className="flex gap-2 leading-relaxed text-white/65">
                      <span className="mt-1 text-gold">•</span><span>{li}</span>
                    </li>
                  ))}
                </ul>
              )}
              {s.contact && (
                <div className="mt-4 rounded-2xl border border-gold/25 bg-forest-deep p-5 text-sm text-white/70">
                  <div className="font-oswald uppercase tracking-wide text-cream">{BRAND.name}</div>
                  <div className="mt-2">Phone: {BRAND.phone}</div>
                  <div>Support: {BRAND.emails.support}</div>
                  <div>Sales: {BRAND.emails.sales}</div>
                  <div>Address: {BRAND.address}</div>
                </div>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
